import React, { useCallback, useMemo, useState, createRef } from "react";
import { Formik, Form } from "formik";
import { useQuery } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ItemCountBadge from "./ItemCountBadge";
import TextField from "@mui/material/TextField";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";

import Autocomplete from "src/components/form/fields5/Autocomplete";
import Input from "src/components/form/Input";
import InternalWorkOrderItems from "./InternalWorkOrderItems";
import AddInternalWorkOrderAsset from "./AddInternalWorkOrderAsset";
import InternalWorkOrderHeader from "./InternalWorkOrderHeader";
import {
  INTERNAL_WORK_ORDER_QUERY,
  FETCH_PROJECT_QUERY,
  USER_COMPANY_INTERNAL_WORK_ORDER_TYPES_QUERY,
  WAREHOUSE_NAMES,
} from "src/constants/graphql/queries";
import useMutation from "src/hooks/useMutation";
import { UPDATE_INTERNAL_WORK_ORDER_MUTATION } from "src/constants/graphql/mutations";
import { useCustomFields } from "../../../contexts/customFields";
import useNotification from "src/hooks/notification";
import getBillingLabel from "src/utils/billing/getBillingLabel";
import useUnsavedChanges from "src/hooks/useUnsavedChanges";
import UnsavedChangesDialog from "src/components/modals/UnsavedChanges/UnsavedChangesDialog";

const getInternalWorkOrderTypeOption = (internalWorkOrderType = "", internalWorkOrderTypes) => {
  return {
    value: internalWorkOrderType,
    label:
      internalWorkOrderTypes.find(type => type.value === internalWorkOrderType)?.label ||
      internalWorkOrderType,
  };
};

export default function InternalWorkOrderEditModal({ id, onClose }) {
  const [openDate, setOpenDate] = useState(false);
  const [display] = useCustomFields();
  const { notifySuccess } = useNotification();
  const { data: { internalWorkOrder = {} } = {}, loading, refetch } = useQuery(
    INTERNAL_WORK_ORDER_QUERY,
    {
      variables: { id },
    }
  );

  const projectId = internalWorkOrder?.project?.id;
  const { data: { project = {} } = {}, loading: loadingProject } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });

  const [saveInternalWorkOrder, { loading: saving }] = useMutation(
    UPDATE_INTERNAL_WORK_ORDER_MUTATION
  );

  const { data: { user = {} } = {} } = useQuery(USER_COMPANY_INTERNAL_WORK_ORDER_TYPES_QUERY);
  const internalWorkOrderTypes = user?.company?.internalWorkOrderTypes || [];

  const hasWarehouseTransferOption = internalWorkOrderTypes.some(
    type => (typeof type === "string" ? type : type?.value) === "warehouse_transfer"
  );

  const { data: { warehouses: allWarehouses = [] } = {} } = useQuery(WAREHOUSE_NAMES, {
    variables: { userWarehouses: true },
    skip: !hasWarehouseTransferOption,
  });

  const warehouses = useMemo(() => project?.warehouses || [], [project]);
  const defaultWarehouseOption = useMemo(() => {
    const selected = internalWorkOrder?.warehouse;
    if (!selected) return null;
    return warehouses.find(w => w.id === selected.id) || selected;
  }, [internalWorkOrder, warehouses]);

  const defaultDestinationWarehouseOption = useMemo(() => {
    const selected = internalWorkOrder?.destinationWarehouse;
    if (!selected) return null;
    return warehouses.find(w => w.id === selected.id) || selected;
  }, [internalWorkOrder, warehouses]);

  const costCenters = useMemo(() => project?.costCenters || [], [project]);
  const shouldShowCostCenterField = costCenters.length > 1 || internalWorkOrder?.costCenter;
  const defaultCostCenterOption = useMemo(() => {
    const selected = internalWorkOrder?.costCenter;
    if (!selected) return null;
    return costCenters.find(cc => cc.id === selected.id) || selected;
  }, [internalWorkOrder, costCenters]);

  const internalBillingTypes = [
    "Flat Rate/As Quoted (Internal Work Orders)",
    "Time and Materials (Blended Rate)",
    "Time and Materials (Itemized Rates)",
    "Not Applicable",
  ];

  // Extract flat rate amount from billing_quantities if it exists
  const extractFlatRateAmount = useCallback(() => {
    const billingQuantities = internalWorkOrder?.billingQuantities;
    if (!billingQuantities) return "";
    // After billing refactor, billingQuantities can be either an array or an object
    if (Array.isArray(billingQuantities)) {
      const flatRateEntry = billingQuantities.find(
        q => q.quantity_type === "flat_rate" || q.quantityType === "flat_rate"
      );
      return flatRateEntry?.quantity_count || flatRateEntry?.quantityCount || "";
    }
    // If it's an object (new format), check for flat_rate or flat_rate properties
    return billingQuantities.flat_rate || billingQuantities.flatRate || "";
  }, [internalWorkOrder]);

  const initialValues = useMemo(
    () => ({
      warehouse: defaultWarehouseOption || null,
      destinationWarehouse: defaultDestinationWarehouseOption || null,
      internalWorkOrderType: getInternalWorkOrderTypeOption(
        internalWorkOrder?.internalWorkOrderType,
        internalWorkOrderTypes
      ),
      costCenter: defaultCostCenterOption || null,
      deadlineDate: internalWorkOrder?.deadlineDate ? moment(internalWorkOrder.deadlineDate) : null,
      comment: internalWorkOrder?.comment || "",
      createdByEmail: internalWorkOrder?.user?.email || "",
      billingType: internalWorkOrder?.billingType || "",
      flatRateAmount: extractFlatRateAmount(),
    }),
    [
      defaultWarehouseOption,
      defaultDestinationWarehouseOption,
      defaultCostCenterOption,
      internalWorkOrder,
      extractFlatRateAmount,
    ]
  );

  const handleSubmit = useCallback(
    async values => {
      const workOrderTypeValue =
        typeof values.internalWorkOrderType === "string"
          ? values.internalWorkOrderType
          : values.internalWorkOrderType?.value;

      // Transform flat rate amount into billing_quantities format if applicable
      let billingQuantities = undefined;
      if (
        values.billingType === "Flat Rate/As Quoted (Internal Work Orders)" &&
        values.flatRateAmount
      ) {
        billingQuantities = [
          {
            quantity_type: "flat_rate",
            quantity_count: parseFloat(values.flatRateAmount),
          },
        ];
      }

      const input = {
        warehouseId: values.warehouse?.warehouseId,
        destinationWarehouseId: values.destinationWarehouse?.warehouseId,
        costCenterId: values.costCenter?.id || undefined,
        internalWorkOrderType: workOrderTypeValue || undefined,
        deadlineDate: values.deadlineDate ? moment(values.deadlineDate).format("YYYY-MM-DD") : null,
        comment: values.comment,
        billingType: values.billingType || undefined,
        billingQuantities: billingQuantities,
      };

      await saveInternalWorkOrder({
        variables: { id: internalWorkOrder.id, input },
        onSuccess: () => {
          notifySuccess("Internal Work Order saved successfully");
          refetch();
        },
      });
    },
    [internalWorkOrder?.id, saveInternalWorkOrder, notifySuccess, refetch]
  );

  const handleAddAsset = useCallback(
    newItem => {
      if (newItem) {
        return {
          ...newItem,
          inventoryRequestId: internalWorkOrder.id,
        };
      }
      return newItem;
    },
    [internalWorkOrder?.id]
  );

  const changeStatus = useCallback(
    key => checked => {
      const input = {
        vaStatus: {
          [key]: checked,
        },
      };
      saveInternalWorkOrder({
        variables: { id: internalWorkOrder.id, input },
        onSuccess: () => {
          refetch();
        },
      });
    },
    [saveInternalWorkOrder, internalWorkOrder, refetch]
  );

  const formikRef = createRef();
  const {
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving: savingUnsaved,
  } = useUnsavedChanges({
    onClose,
    isDirtyFn: () => !!formikRef.current?.dirty,
    onSave: async () => {
      const submitFn = formikRef.current?.submitForm;
      if (submitFn) submitFn();
    },
  });

  const title = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={1}
      position="relative"
      width="100%"
    >
      <Typography color="textPrimary" fontSize={24} fontWeight={500}>
        Edit Internal Work Order
      </Typography>
      <ItemCountBadge count={internalWorkOrder?.internalWorkOrderItems?.length || 0} />
      <IconButton
        onClick={attemptClose}
        size="small"
        sx={{ position: "absolute", right: 0, top: 0 }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );

  if (loading || loadingProject) {
    return (
      <Dialog open fullWidth maxWidth="md" onClose={attemptClose} aria-labelledby="dialog-title">
        <DialogTitle id="dialog-title">{title}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {[...Array(10)].map((_, i) => (
              <Grid key={i} size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" height={40} />
              </Grid>
            ))}
            <Grid size={12}>
              <Skeleton variant="rectangular" height={160} />
            </Grid>
          </Grid>
          <Box mt={2} />
        </DialogContent>
        {showDialog && (
          <UnsavedChangesDialog
            open={showDialog}
            onCancel={cancelDiscard}
            onDiscard={confirmDiscard}
            onSave={confirmSave}
            saving={savingUnsaved}
          />
        )}
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={attemptClose}
      aria-labelledby="dialog-title"
      PaperProps={{
        sx: {
          height: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={values => {
          const errors = {};
          if (
            values.billingType === "Flat Rate/As Quoted (Internal Work Orders)" &&
            !values.flatRateAmount
          ) {
            errors.flatRateAmount = "Flat rate amount is required";
          }
          if (
            shouldShowCostCenterField &&
            values.billingType &&
            values.billingType !== "Not Applicable" &&
            costCenters.length > 1 &&
            !values.costCenter
          ) {
            errors.costCenter = "Cost center is required";
          }
          return errors;
        }}
      >
        {({ values, setFieldValue, submitForm, isSubmitting }) => {
          const workOrderTypeValue =
            typeof values.internalWorkOrderType === "string"
              ? values.internalWorkOrderType
              : values.internalWorkOrderType?.value;
          const showDestinationWarehouse = workOrderTypeValue === "warehouse_transfer";
          const hasInternalWorkOrderItems =
            Array.isArray(internalWorkOrder?.internalWorkOrderItems) &&
            internalWorkOrder.internalWorkOrderItems.length > 0;
          const showAssignedWarehouse = showDestinationWarehouse || !hasInternalWorkOrderItems;
          const showFlatRateAmount =
            values.billingType === "Flat Rate/As Quoted (Internal Work Orders)";
          const showCostCenterField =
            shouldShowCostCenterField &&
            values.billingType &&
            values.billingType !== "Not Applicable";

          return (
            <Form style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <DialogTitle id="dialog-title">{title}</DialogTitle>
              <DialogContent sx={{ flexGrow: 1, overflowY: "auto" }}>
                <InternalWorkOrderHeader
                  internalWorkOrder={internalWorkOrder}
                  changeStatus={changeStatus}
                />

                <DialogContentText>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {showAssignedWarehouse && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Autocomplete
                          name="warehouse"
                          label="Assigned Warehouse"
                          options={warehouses}
                          getOptionLabel={option => option?.name || ""}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        />
                      </Grid>
                    )}
                    {showDestinationWarehouse && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Autocomplete
                          name="destinationWarehouse"
                          label="Destination Warehouse"
                          options={allWarehouses.filter(
                            w => w?.warehouseId !== values.warehouse?.warehouseId
                          )}
                          getOptionLabel={option => option?.name || ""}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        />
                      </Grid>
                    )}
                    {internalWorkOrderTypes.length > 0 && (
                      <Grid size={12}>
                        <Autocomplete
                          key={`work-order-type-${internalWorkOrderTypes.length}`}
                          name="internalWorkOrderType"
                          label="Internal Work Order Type"
                          options={internalWorkOrderTypes}
                          getOptionLabel={option => {
                            const str = typeof option === "string" ? option : (option?.value || "");
                            return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                          }}
                          isOptionEqualToValue={(option, value) => {
                            const optionVal = typeof option === "string" ? option : option?.value;
                            const valueVal = typeof value === "string" ? value : value?.value;
                            return optionVal === valueVal;
                          }}
                          onChange={(e, newValue) => {
                            const valueToStore =
                              typeof newValue === "string" ? newValue : newValue?.value;
                            setFieldValue("internalWorkOrderType", valueToStore);
                          }}
                          freeSolo
                          forcePopupIcon
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          open={openDate}
                          onOpen={() => setOpenDate(true)}
                          onClose={() => setOpenDate(false)}
                          label="Complete By Date"
                          value={values.deadlineDate}
                          onChange={date => setFieldValue("deadlineDate", date)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              onClick: () => setOpenDate(true),
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        disabled
                        label="Created By"
                        value={values.createdByEmail}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    {internalBillingTypes && internalBillingTypes.length > 0 && (
                      <Grid size={12}>
                        <Autocomplete
                          name="billingType"
                          label="Billing Type"
                          options={internalBillingTypes}
                          getOptionLabel={option => getBillingLabel(option) || option}
                          isOptionEqualToValue={(option, value) => option === value}
                        />
                      </Grid>
                    )}
                    {showFlatRateAmount && (
                      <Grid size={12}>
                        <Input
                          name="flatRateAmount"
                          type="number"
                          step="0.01"
                          label="Flat Rate Amount"
                          required
                        />
                      </Grid>
                    )}
                    {showCostCenterField && (
                      <Grid size={12}>
                        <Autocomplete
                          key={`cost-center-${costCenters.length}`}
                          name="costCenter"
                          label="Cost Center"
                          options={costCenters}
                          getOptionLabel={option => option?.costCenterName || ""}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          required={costCenters.length > 1}
                          forcePopupIcon
                        />
                      </Grid>
                    )}
                    <Grid size={12}>
                      <Input name="comment" type="text" label="Comments" />
                    </Grid>

                    {Array.isArray(internalWorkOrder?.internalWorkOrderItems) &&
                      internalWorkOrder.internalWorkOrderItems.length > 0 && (
                        <Grid size={12}>
                          <Box mt={2}>
                            <InternalWorkOrderItems
                              display={display}
                              items={internalWorkOrder.internalWorkOrderItems}
                              onDelete={refetch}
                              internalWorkOrderId={internalWorkOrder.id}
                              internalWorkOrder={internalWorkOrder}
                            />
                          </Box>
                        </Grid>
                      )}

                    <Grid size={12}>
                      <AddInternalWorkOrderAsset
                        projectId={internalWorkOrder?.project?.id}
                        refetch={refetch}
                        processNewItem={handleAddAsset}
                        internalWorkOrderId={internalWorkOrder?.id}
                      />
                    </Grid>
                  </Grid>
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Box display="flex" justifyContent="flex-end" width="100%" p={2} pr={3}>
                  <Box display="flex" gap={1}>
                    <Button variant="outlined" onClick={attemptClose} color="primary">
                      Close
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={submitForm}
                      disabled={isSubmitting || saving}
                      startIcon={(isSubmitting || saving) && <CircularProgress size={22} />}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
      {showDialog && (
        <UnsavedChangesDialog
          open={showDialog}
          onCancel={cancelDiscard}
          onDiscard={confirmDiscard}
          onSave={confirmSave}
          saving={savingUnsaved}
        />
      )}
    </Dialog>
  );
}
